import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, PieChart, Pie,
} from "recharts";
import { CalendarSelector } from "views/GlAnalytics/DayWeekMonthPicker";
import { Sun, Factory, LayoutGrid, Plug, Building2, Zap } from "lucide-react";
import api from "../../api";

// ── Brand colours ─────────────────────────────────────────────────────────────
const C_SOLAR = "#1ec8c8";
const C_GRID = "#ffa726";
const C_DG = "#90a4ae";
const C_BLUE = "#0123b4";

const DEVICE_SERIES = [
    { key: "solar", type: "Solar" },
    { key: "grid", type: "Transformer" },
    { key: "dgLoad", type: "DG" },
];

const SERIES_CONFIG = [
    { source: "Solar", dataKey: "solar", name: "Solar Energy (kWh)", color: C_SOLAR, gradId: "gSolar", yAxisId: "L" },
    { source: "Grid", dataKey: "grid", name: "Grid Energy (kWh)", color: C_GRID, gradId: "gGrid", yAxisId: "L" },
    { source: "DG", dataKey: "dgLoad", name: "DG Energy (kWh)", color: C_DG, gradId: "gDg", yAxisId: "R" },
];


// ── Inline SVG icons ──────────────────────────────────────────────────────────


// ── Card helpers ──────────────────────────────────────────────────────────────
const SideCard = ({ icon, value, label }) => (
    <div style={{
        border: "1px solid #e8eaf0", borderRadius: 6,
        padding: "4px", textAlign: "center", background: "#fff",
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", gap: 1, minHeight: 52,
    }}>
        {icon}
        <div style={{ fontWeight: 700, fontSize: 9, color: "#222", lineHeight: 1.2 }}>{value}</div>
        <div style={{ fontSize: 8.5, color: "#aaa" }}>{label}</div>
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
export default function EnergyMonitoring() {
    const buildingID = useSelector((s) => s.isLogged.data.building.id);

    const [activeSource, setActiveSource] = useState("All");
    const [sourceDropdownOpen, setSourceDropdownOpen] = useState(false);
    const [downloadDropdownOpen, setDownloadDropdownOpen] = useState(false);
    const chartWrapperRef = useRef(null);
    const [dateMode, setDateMode] = useState("day");
    const [selectedDate, setSelectedDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        return d.toISOString().split("T")[0];
    });

    const parseDateInput = (input) => {
        if (!input) return input;
        if (typeof input === "object" && input.startDate) {
            const p = input.startDate.split("/"); return `${p[2]}-${p[1]}-${p[0]}`;
        }
        if (input instanceof Date) return input.toISOString().split("T")[0];
        if (typeof input === "string" && input.includes("/")) {
            const p = input.split("/"); return `${p[2]}-${p[1]}-${p[0]}`;
        }
        return input;
    };
    const handleDateSelect = (d) => { setDateMode("day"); setSelectedDate(parseDateInput(d)); };
    const handleWeekSelect = (w) => { setDateMode("week"); setSelectedDate(parseDateInput(w)); };
    const handleMonthSelect = (m) => { setDateMode("month"); setSelectedDate(parseDateInput(m)); };
    const handleYearSelect = (y) => { setDateMode("year"); setSelectedDate(parseDateInput(y)); };

    const [lifetimeDemand, setLifetimeDemand] = useState("-");
    const [todayKwh, setTodayKwh] = useState("-");
    const [energyToday, setEnergyToday] = useState({ solar: "-", grid: "-", dg: "-" });
    const [currentPower, setCurrentPower] = useState({ solar: "-", grid: "-", dg: "-" });
    const [rawEnergyToday, setRawEnergyToday] = useState({ solar: 0, grid: 0, dg: 0 });
    const [chartData, setChartData] = useState([]);
    const [loadingChart, setLoadingChart] = useState(false);

    const energyTotal = rawEnergyToday.solar + rawEnergyToday.grid + rawEnergyToday.dg;
    const isPowerSourceEmpty = energyTotal <= 0;
    const powerSourcePct = [
        { name: "Solar", value: energyTotal > 0 ? +(rawEnergyToday.solar / energyTotal * 100).toFixed(2) : 0, kwh: rawEnergyToday.solar, fill: C_SOLAR },
        { name: "Grid", value: energyTotal > 0 ? +(rawEnergyToday.grid / energyTotal * 100).toFixed(2) : 0, kwh: rawEnergyToday.grid, fill: C_GRID },
        { name: "DG", value: energyTotal > 0 ? +(rawEnergyToday.dg / energyTotal * 100).toFixed(2) : 0, kwh: rawEnergyToday.dg, fill: C_DG },
    ];

    // ── Today (sum of today's daily buckets) & Current (latest bucket) ─────────
    useEffect(() => {
        const today = new Date().toISOString().split("T")[0];
        Promise.all(
            DEVICE_SERIES.map(({ type }) => api.dashboard.energyByDeviceType(type, "day", today))
        ).then((results) => {
            const totals = {};
            const latest = {};
            DEVICE_SERIES.forEach(({ key }, idx) => {
                const res = results[idx];
                const rows = res?.success && Array.isArray(res.data) ? res.data : [];
                totals[key] = rows.reduce((sum, r) => sum + (parseFloat(r?.energy_kwh) || 0), 0);
                const lastRow = rows[rows.length - 1];
                latest[key] = lastRow?.energy_kwh != null ? parseFloat(lastRow.energy_kwh) : 0;
            });

            setEnergyToday({
                solar: totals.solar.toFixed(2) + " KWh",
                grid: totals.grid > 999 ? (totals.grid / 1000).toFixed(2) + " MWh" : totals.grid.toFixed(2) + " KWh",
                dg: totals.dgLoad.toFixed(2) + " KWh",
            });
            setRawEnergyToday({ solar: totals.solar, grid: totals.grid, dg: totals.dgLoad });

            setCurrentPower({
                solar: latest.solar.toFixed(2) + " KWh",
                grid: latest.grid.toFixed(2) + " KWh",
                dg: latest.dgLoad.toFixed(2) + " KWh",
            });
        }).catch(() => {
            setEnergyToday({ solar: "-", grid: "-", dg: "-" });
            setCurrentPower({ solar: "-", grid: "-", dg: "-" });
            setRawEnergyToday({ solar: 0, grid: 0, dg: 0 });
        });
    }, [buildingID]);

    useEffect(() => {
        api.dashboard.getPlantLifetimeEnergy()
            .then((res) => setLifetimeDemand(res?.success && res.cumulative_kwh != null ? parseFloat(res.cumulative_kwh).toFixed(2) : "-"))
            .catch(() => setLifetimeDemand("-"));
    }, [buildingID]);

    useEffect(() => {
        api.dashboard.todayTotalEnergy()
            .then((res) => setTodayKwh(res?.success && res.total_kwh != null ? parseFloat(res.total_kwh).toFixed(2) : "-"))
            .catch(() => setTodayKwh("-"));
    }, [buildingID]);

    // ── Chart fetch ─────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!selectedDate) return;
        setLoadingChart(true);
        const fetchers = DEVICE_SERIES.map(({ type }) =>
            dateMode === "month"
                ? api.dashboard.energyByDeviceTypeMonthly(type, selectedDate)
                : api.dashboard.energyByDeviceType(type, dateMode, selectedDate)
        );
        Promise.all(fetchers)
            .then((results) => {
                const base = results.find((r) => r?.success && Array.isArray(r.data))?.data || [];
                const merged = base.map((row, i) => {
                    const point = { label: row.label };
                    DEVICE_SERIES.forEach(({ key }, idx) => {
                        const res = results[idx];
                        const val = res?.success && Array.isArray(res.data) ? res.data[i]?.energy_kwh : undefined;
                        point[key] = val != null ? +parseFloat(val).toFixed(2) : 0;
                    });
                    return point;
                });
                setChartData(merged);
            })
            .catch(() => setChartData([]))
            .finally(() => setLoadingChart(false));
    }, [selectedDate, dateMode]);

    const chartTitle = dateMode === "day" ? "Daily Energy (kWh)" : dateMode === "week" ? "Weekly Energy (kWh)" : dateMode === "month" ? "Monthly Energy (kWh)" : "Yearly Energy (kWh)";

    const visibleSeries = activeSource === "All" ? SERIES_CONFIG : SERIES_CONFIG.filter((s) => s.source === activeSource);

    const formatXTick = (value) => {
        if (dateMode !== "month") return value;
        const d = new Date(value);
        return isNaN(d.getTime()) ? value : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    };

    const downloadChartPNG = () => {
        const svgs = Array.from(chartWrapperRef.current?.querySelectorAll("svg") || []);
        const svgEl = svgs.sort((a, b) => (b.clientWidth * b.clientHeight) - (a.clientWidth * a.clientHeight))[0];
        if (!svgEl) return;
        const { clientWidth: w, clientHeight: h } = svgEl;
        const svgData = new XMLSerializer().serializeToString(svgEl);
        const svgUrl = URL.createObjectURL(new Blob([svgData], { type: "image/svg+xml;charset=utf-8" }));
        const img = new Image();
        img.onload = () => {
            const scale = 2;
            const canvas = document.createElement("canvas");
            canvas.width = w * scale;
            canvas.height = h * scale;
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.scale(scale, scale);
            ctx.drawImage(img, 0, 0, w, h);
            URL.revokeObjectURL(svgUrl);
            canvas.toBlob((blob) => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `energy-${dateMode}-${selectedDate}.png`;
                link.click();
                URL.revokeObjectURL(link.href);
            });
        };
        img.src = svgUrl;
    };

    const downloadDataCSV = () => {
        if (!chartData.length) return;
        const headers = ["Label", ...visibleSeries.map((s) => s.name)];
        const rows = chartData.map((row) => [row.label, ...visibleSeries.map((s) => (+(row[s.dataKey] ?? 0)).toFixed(2))]);
        const csvContent = [[chartTitle], headers, ...rows].map((r) => r.join(",")).join("\n");
        const link = document.createElement("a");
        link.href = URL.createObjectURL(new Blob([csvContent], { type: "text/csv;charset=utf-8;" }));
        link.download = `energy-${dateMode}-${selectedDate}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    const handleDownloadSelect = (format) => {
        setDownloadDropdownOpen(false);
        if (format === "PNG") downloadChartPNG();
        else if (format === "CSV") downloadDataCSV();
    };

    // ── Render ───────────────────────────────────────────────────────────────────
    return (
        <div style={{ height: "calc(103vh - 116px)", display: "flex", flexDirection: "column", overflow: "hidden" }}>

            {/* ═══════════════ MAIN: sidebar + right panel ═══════════════ */}
            <div style={{ display: "flex", gap: 6, flex: 1, minHeight: 0, overflow: "hidden" }}>

                {/* ──────────── LEFT SIDEBAR ──────────── */}
                <div style={{
                    width: 215, flexShrink: 0,
                    background: "#fff", border: "1px solid #e0e0e0",
                    padding: "10px",
                    display: "flex", flexDirection: "column",
                    overflowY: "auto",
                    borderRadius: "8px"
                }}>

                    {/* Source Tabs */}
                    <div style={{ display: "flex", justifyContent: "space-around", paddingBottom: 8, marginBottom: 8, borderBottom: "1px solid #f0f0f0" }}>
                        {[{ key: "Solar", col: C_SOLAR }, { key: "Grid", col: C_GRID }, { key: "DG", col: C_DG }].map(({ key, col }) => (
                            <button
                                key={key}
                                onClick={() => setActiveSource(key)}
                                style={{
                                    display: "flex", flexDirection: "column", alignItems: "center",
                                    background: "none", border: "none", cursor: "pointer", padding: "4px 10px",
                                    borderBottom: activeSource === key ? `2px solid ${C_BLUE}` : "2px solid transparent",
                                }}
                            >
                                <div style={{ width: 15, height: 15, borderRadius: "50%", background: col, marginBottom: 4 }} />
                                <span style={{ fontSize: 10, fontWeight: 700, color: activeSource === key ? C_BLUE : "#888" }}>
                                    {key.toUpperCase()}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Sites Monitored */}
                    <div style={{ color: C_BLUE, fontWeight: 700, fontSize: 10.5, marginBottom: 4 }}>Sites Monitored</div>
                    <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
                        <div style={{ flex: 1, border: "1px solid #e8eaf0", borderRadius: 6, padding: "6px 4px", textAlign: "center" }}>
                            <Sun size={24} color={C_SOLAR} strokeWidth={1.5} />
                            <div style={{ fontSize: 9, color: "#555" }}>Solar: -</div>
                            <div style={{ fontSize: 9, color: "#999" }}>Capacity: -</div>
                        </div>
                        <div style={{ flex: 1, border: "1px solid #e8eaf0", borderRadius: 6, padding: "6px 4px", textAlign: "center" }}>
                            <Factory size={24} color="#90a4ae" strokeWidth={1.5} />
                            <div style={{ fontSize: 9, color: "#555" }}>Non Solar: -</div>
                            <div style={{ fontSize: 9, color: "#999" }}>Capacity: -</div>
                        </div>
                    </div>

                    {/* Column headers */}
                    <div style={{ display: "flex", gap: 4, marginBottom: 3 }}>
                        <div style={{ flex: 1, color: C_BLUE, fontWeight: 700, fontSize: 10 }}>Energy Today</div>
                        <div style={{ flex: 1, color: C_BLUE, fontWeight: 700, fontSize: 10 }}>Current Power</div>
                    </div>

                    {/* All 6 energy/power cards — single grid keeps all rows equal height */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gridAutoRows: "1fr", gap: 4, marginBottom: 4 }}>
                        <SideCard icon={<Sun size={16} color={C_SOLAR} strokeWidth={1.5} />} value={energyToday.solar} label="Solar" />
                        <SideCard icon={<Sun size={16} color={C_SOLAR} strokeWidth={1.5} />} value={currentPower.solar} label="Solar" />
                        <SideCard icon={<LayoutGrid size={16} color="#bdbdbd" strokeWidth={1.5} />} value={energyToday.grid} label="Grid" />
                        <SideCard icon={<LayoutGrid size={16} color="#bdbdbd" strokeWidth={1.5} />} value={currentPower.grid} label="Grid" />
                        <SideCard icon={<Plug size={16} color="#bdbdbd" strokeWidth={1.5} />} value={energyToday.dg} label="DG" />
                        <SideCard icon={<Plug size={16} color="#bdbdbd" strokeWidth={1.5} />} value={currentPower.dg} label="DG" />
                    </div>

                    {/* Power Sources */}
                    <div style={{ color: C_BLUE, fontWeight: 700, fontSize: 10.5, marginBottom: 4 }}>Power Sources</div>
                    <div style={{ flex: 1, border: "1px solid #e8eaf0", borderRadius: 6, padding: "8px", minHeight: 90, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {powerSourcePct.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Tooltip
                                        formatter={(value, name, entry) => entry.payload.kwh != null ? [`${entry.payload.kwh.toFixed(2)} kWh`, name] : ["No data", name]}
                                        contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e8eaf0", padding: "4px 10px" }}
                                        position={{ y: 0 }}
                                        wrapperStyle={{ zIndex: 10 }}
                                    />
                                    {isPowerSourceEmpty && (
                                        <Pie
                                            data={[{ name: "No data", value: 1 }]}
                                            cx="38%" cy="50%"
                                            innerRadius="48%" outerRadius="75%"
                                            dataKey="value" startAngle={90} endAngle={-270}
                                            fill="#e0e0e0" stroke="none"
                                            isAnimationActive={false}
                                        />
                                    )}
                                    <Pie
                                        data={powerSourcePct}
                                        cx="38%" cy="50%"
                                        innerRadius="48%" outerRadius="75%"
                                        dataKey="value" startAngle={90} endAngle={-270} stroke="none"
                                    />
                                    <Legend
                                        layout="vertical" align="right" verticalAlign="middle"
                                        iconSize={6} iconType="circle"
                                        formatter={(value, entry) => (
                                            <span style={{ fontSize: 8, color: "#555" }}>{value}{value !== "No data" && <>&nbsp;&nbsp;{entry.payload.value}%</>}</span>
                                        )}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <span style={{ fontSize: 10, color: "#bdbdbd" }}>-</span>
                        )}
                    </div>

                </div>

                {/* ──────────── RIGHT PANEL ──────────── */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6, minHeight: 0 }}>

                    {/* ── Top controls row ── */}
                    <div style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        flexWrap: "wrap", gap: 8,
                    }}>
                        {/* Stat chips */}
                        <div style={{ display: "flex", gap: 5, alignItems: "stretch" }}>
                            {[
                                { icon: <Building2 size={22} color={C_BLUE} strokeWidth={1.5} />, value: lifetimeDemand, label: "Lifetime Generation", color: C_BLUE },
                                { icon: <Zap size={22} color={C_BLUE} strokeWidth={1.5} />, value: todayKwh, label: "Today KWH", color: C_BLUE },
                            ].map(({ icon, value, label, color }) => (
                                <div key={label} style={{
                                    display: "flex", alignItems: "center", gap: 5,
                                    border: "1px solid #e8eaf0", borderRadius: 8,
                                    padding: "0 14px", background: "#fff",
                                    height: 40, boxSizing: "border-box", minWidth: 150,
                                }}>
                                    {icon}
                                    <div>
                                        <div style={{ fontWeight: 800, fontSize: 10.5, color, lineHeight: 1.1 }}>{value}</div>
                                        <div style={{ fontSize: 9, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.4px" }}>{label}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Controls */}
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {/* Solar custom dropdown */}
                            <div style={{ position: "relative", height: 40, width: 120, boxSizing: "border-box" }}>
                                <div
                                    onClick={() => { setDownloadDropdownOpen(false); setSourceDropdownOpen(o => !o); }}
                                    style={{
                                        display: "flex", alignItems: "center", justifyContent: "space-between",
                                        border: "1px solid #ccc", borderRadius: 6, height: "100%",
                                        backgroundColor: "#fff", padding: "0 10px", cursor: "pointer",
                                    }}
                                >
                                    <span style={{ fontSize: 10.5, fontWeight: 600, color: "#333" }}>{activeSource}</span>
                                    <svg width="14" height="14" viewBox="0 0 24 24"><path fill="#555" d="M7 10l5 5 5-5z" /></svg>
                                </div>
                                {sourceDropdownOpen && (
                                    <div style={{
                                        position: "absolute", top: "calc(100% + 4px)", left: 0, width: "100%",
                                        background: "#fff", border: "1px solid #ccc", borderRadius: 6,
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 999,
                                    }}>
                                        {["All", "Solar", "Grid", "DG"].map(opt => (
                                            <div
                                                key={opt}
                                                onClick={() => { setActiveSource(opt); setSourceDropdownOpen(false); }}
                                                style={{
                                                    padding: "10px 14px", fontSize: 10.5, fontWeight: 500,
                                                    cursor: "pointer", color: activeSource === opt ? "#2563eb" : "#333",
                                                    backgroundColor: activeSource === opt ? "#eff6ff" : "transparent",
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.backgroundColor = activeSource === opt ? "#eff6ff" : "#f3f4f6"}
                                                onMouseLeave={e => e.currentTarget.style.backgroundColor = activeSource === opt ? "#eff6ff" : "transparent"}
                                            >
                                                {opt}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Download dropdown */}
                            <div style={{ position: "relative", height: 40, boxSizing: "border-box" }}>
                                <div
                                    onClick={() => { setSourceDropdownOpen(false); setDownloadDropdownOpen((o) => !o); }}
                                    style={{
                                        display: "flex", alignItems: "center", gap: 5,
                                        border: "1px solid #ccc", borderRadius: 6, padding: "0 12px",
                                        fontSize: 10.5, fontWeight: 500, background: "#fff", cursor: "pointer", color: "#444",
                                        height: "100%", boxSizing: "border-box",
                                    }}
                                >
                                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                                        <path d="M6.5 1v7M3.5 6l3 3 3-3" stroke="#555" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M1 11h11" stroke="#555" strokeWidth="1.4" strokeLinecap="round" />
                                    </svg>
                                    Download
                                </div>
                                {downloadDropdownOpen && (
                                    <div style={{
                                        position: "absolute", top: "calc(100% + 4px)", right: 0, minWidth: 110,
                                        background: "#fff", border: "1px solid #ccc", borderRadius: 6,
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 999,
                                    }}>
                                        {["PNG", "CSV"].map((opt) => (
                                            <div
                                                key={opt}
                                                onClick={() => handleDownloadSelect(opt)}
                                                style={{
                                                    padding: "10px 14px", fontSize: 10.5, fontWeight: 500,
                                                    cursor: "pointer", color: "#333",
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f3f4f6"}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                                            >
                                                {opt}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <CalendarSelector
                                defaultMode="day"
                                onDateSelect={handleDateSelect}
                                onWeekSelect={handleWeekSelect}
                                onMonthSelect={handleMonthSelect}
                                onYearSelect={handleYearSelect}
                                showSelection={false}
                            />
                        </div>
                    </div>

                    {/* ── Chart card ── */}
                    <div style={{
                        flex: 1, background: "#fff", borderRadius: 8,
                        border: "1px solid #e0e0e0", padding: "12px 14px",
                        display: "flex", flexDirection: "column", minHeight: 0,
                    }}>
                        <div style={{ fontSize: 10.5, fontWeight: 600, color: C_BLUE, marginBottom: 8 }}>
                            {chartTitle}
                        </div>

                        <div ref={chartWrapperRef} style={{ flex: 1, minHeight: 0 }}>
                            {loadingChart ? (
                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                    <span style={{ color: "#bdbdbd", fontSize: 13 }}>Loading…</span>
                                </div>
                            ) : chartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={chartData} margin={{ top: 4, right: 40, left: 10, bottom: dateMode === "month" || dateMode === "day" ? 15 : 0 }}>
                                        <defs>
                                            {visibleSeries.map(({ gradId, color }) => (
                                                <linearGradient key={gradId} id={gradId} x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor={color} stopOpacity={0.65} />
                                                    <stop offset="95%" stopColor={color} stopOpacity={0.04} />
                                                </linearGradient>
                                            ))}
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" vertical={false} />
                                        <XAxis
                                            dataKey="label"
                                            tickFormatter={formatXTick}
                                            tick={{ fontSize: 8.5, fill: "#bdbdbd" }}
                                            axisLine={false} tickLine={false}
                                            interval={dateMode === "month" || dateMode === "day" ? 0 : "preserveStartEnd"}
                                            angle={dateMode === "month" || dateMode === "day" ? -90 : 0}
                                            textAnchor={dateMode === "month" || dateMode === "day" ? "end" : "middle"}
                                            tickMargin={dateMode === "month" || dateMode === "day" ? 14 : 6}
                                            height={dateMode === "month" || dateMode === "day" ? 52 : 30}
                                        />
                                        <YAxis yAxisId="L" label={{ value: "Energy (kWh)", angle: -90, position: "insideLeft", dx: -10, style: { fontSize: 11, fill: "#555", fontWeight: 600 } }} tick={{ fontSize: 9, fill: "#bdbdbd" }} axisLine={false} tickLine={false} width={55} />
                                        <YAxis yAxisId="R" orientation="right" tick={{ fontSize: 9, fill: "#bdbdbd" }} axisLine={false} tickLine={false} />
                                        <Tooltip
                                            formatter={(value) => typeof value === "number" ? value.toFixed(2) : value}
                                            contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e8eaf0" }}
                                        />
                                        <Legend iconSize={10} iconType="circle" wrapperStyle={{ fontSize: 10.5, paddingTop: 8 }} />
                                        {visibleSeries.map(({ dataKey, name, color, gradId, yAxisId }) => (
                                            <Area key={dataKey} yAxisId={yAxisId} type="monotone" dataKey={dataKey} name={name} stroke={color} fill={`url(#${gradId})`} strokeWidth={1.5} />
                                        ))}
                                    </AreaChart>
                                </ResponsiveContainer>
                            ) : (
                                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                    <svg width="52" height="40" viewBox="0 0 52 40" fill="none" style={{ marginBottom: 10 }}>
                                        <rect x="0" y="37" width="52" height="2" rx="1" fill="#e8eaf0" />
                                        <polyline points="4,34 12,20 22,28 32,10 46,22" stroke="#e8eaf0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <span style={{ fontSize: 12, fontStyle: "italic", color: "#bdbdbd" }}>
                                        No data for the selected period.
                                    </span>
                                </div>
                            )}
                        </div>{/* end chart flex wrapper */}
                    </div>{/* end chart card */}

                </div>{/* end right panel */}

            </div>{/* end main flex */}
        </div>
    );
}
